import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Button, CircularProgress, Alert, TextField,
} from '@mui/material';
import LocalPrintshopRounded from '@mui/icons-material/LocalPrintshopRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';

import {
  getInkPurchaseOrder, listInkSuppliers, InkPurchaseOrder, InkPoLine, InkSupplier,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

/**
 * InkPurchaseOrderDocument — the printable F-093 purchase order.
 *
 * Rendered as HTML and printed with the browser's own print dialog, exactly like
 * the Ink Sticker. NOT a server-side PDF: Gotenberg is still gated on Container
 * App approval (docs/print-service-plan.md), and the 2025-12-07 incident is a
 * standing reminder not to reach for a headless browser on the Functions host.
 * The markup below is deliberately plain — when Gotenberg does land it can
 * render this same page server-side with no rewrite.
 *
 * The layout reproduces `docs/F-093 Purchase Order.pdf` (Version 2, issued
 * 18-SEP-2018): letterhead block, supplier panel, red column bar, body, totals
 * box, ISO badge and the form's own footer. Two things are approximations and
 * are called out in the demo notes rather than hidden:
 *   - the ABARIS wordmark is set in a heavy sans, not the real logotype (no
 *     vector asset exists anywhere in this repo — drop one in and swap it);
 *   - column widths are matched by eye off the PDF, not extracted from it.
 */

// A4 landscape, which is what F-093 is set on.
const PAGE_W_MM = 297;
const PAGE_H_MM = 210;
const ABARIS_GREEN = '#4CAF3D';
const FORM_RED = '#E8112D';

const GST_RATE = 0.10;

const money = (v: number | null | undefined) =>
  (v == null || !Number.isFinite(v) ? '' : v.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }));

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Whole tubs from kg, when the pack size is known — the PO is placed in tubs. */
function qtyText(line: InkPoLine & { packs_ordered?: number | null; pack_size_kg?: number | null }): string {
  const packs = line.packs_ordered;
  const kg = Number(line.qty_ordered_kg ?? 0);
  if (packs != null && Number.isFinite(Number(packs))) {
    const n = Number(packs);
    return `${n} ${n === 1 ? 'tub' : 'tubs'}`;
  }
  return `${kg} kg`;
}

export default function InkPurchaseOrderDocument() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const poId = Number(params.get('po') || 0);

  const poQ = useQuery({
    queryKey: productionKeys.inkPurchaseOrder(poId),
    queryFn: () => getInkPurchaseOrder(poId),
    enabled: poId > 0,
  });
  const suppliersQ = useQuery({
    queryKey: productionKeys.inkSuppliers(true),
    queryFn: () => listInkSuppliers(true),
    staleTime: 5 * 60 * 1000,
  });

  const po = poQ.data as (InkPurchaseOrder & { lines?: InkPoLine[] }) | undefined;
  const supplier: InkSupplier | undefined = useMemo(
    () => (suppliersQ.data ?? []).find((s) => s.supplier_code === po?.supplier_code),
    [suppliersQ.data, po?.supplier_code],
  );

  // The form has a free "deliver to / attention" line in practice; keep it
  // editable on screen and printed, rather than inventing a field in the DB
  // for something that changes per order.
  const [attention, setAttention] = useState('');

  useEffect(() => {
    document.title = po ? `${po.po_number} — Purchase Order` : 'Purchase Order';
  }, [po]);

  if (!poId) {
    return <Box sx={{ p: 3 }}><Alert severity="error">No purchase order given (expected ?po=&lt;id&gt;).</Alert></Box>;
  }
  if (poQ.isLoading) {
    return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;
  }
  if (poQ.isError || !po) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Couldn’t load that purchase order.</Alert></Box>;
  }

  const lines = po.lines ?? [];
  const subtotal = lines.reduce(
    (t, l) => t + Number(l.qty_ordered_kg ?? 0) * Number(l.unit_cost_per_kg ?? 0), 0,
  );
  const gst = subtotal * GST_RATE;
  const total = subtotal + gst;

  return (
    <Box sx={{ bgcolor: '#f4f3f1', minHeight: '100vh', pb: 6 }}>
      {/* ---- screen-only chrome ---- */}
      <Stack direction="row" alignItems="center" spacing={1.5} className="no-print"
        sx={{ px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 2, bgcolor: TOKENS.panel, borderBottom: `1px solid ${TOKENS.line}` }}>
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/production/ink/orders')}
          sx={{ color: TOKENS.ink3 }}>Orders</Button>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{po.po_number}</Typography>
        <TextField size="small" label="Attention / deliver to" value={attention}
          onChange={(e) => setAttention(e.target.value)} sx={{ width: 300 }} />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<LocalPrintshopRounded />} onClick={() => window.print()}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Print
        </Button>
      </Stack>

      {po.status === 'draft' && (
        <Alert severity="warning" className="no-print" sx={{ m: 2, mb: 0 }}>
          This PO is still a DRAFT — it has no order date and has not been placed with the supplier.
          Issue it before sending this document.
        </Alert>
      )}

      <style>{`
        @page { size: ${PAGE_W_MM}mm ${PAGE_H_MM}mm; margin: 0; }
        @media print {
          /* A stray UA margin shifts the whole sheet down — the "why is this
             off-centre" report the sticker already learned the hard way. */
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          /* Isolate the sheet: the production shell's sidebar and header are
             still in the DOM and would otherwise print down the left edge.
             Same visibility trick the Ink Sticker uses — display:none on an
             ancestor would take the sheet with it. */
          body * { visibility: hidden !important; }
          #f093-root, #f093-root * { visibility: visible !important; }
          #f093-root {
            position: absolute !important; left: 0 !important; top: 0 !important;
            width: 100% !important;
          }
          .no-print { display: none !important; }
          .f093-sheet { box-shadow: none !important; margin: 0 !important; }
          /* The red bar and the green mark ARE the form, not decoration —
             make sure the driver keeps the backgrounds. */
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* ---- the sheet ---- */}
      <Box className="f093-sheet" id="f093-root" sx={{
        width: `${PAGE_W_MM}mm`, minHeight: `${PAGE_H_MM}mm`,
        mx: 'auto', mt: 2, bgcolor: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,.14)',
        p: '12mm 10mm 8mm', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'Helvetica, Arial, sans-serif', color: '#111',
      }}>
        {/* ---- header ---- */}
        <Stack direction="row" alignItems="flex-start" spacing={3}>
          <Box sx={{ width: '78mm', flexShrink: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <AbarisMark />
              <Box sx={{ fontSize: '7.6pt', lineHeight: 1.45, pt: '1mm' }}>
                <div style={{ fontWeight: 700 }}>Abaris Printing &amp; Publishing Co. Pty. Ltd.</div>
                <div>ABN: 35 004 340 685</div>
                <div>38 Park West Drive</div>
                <div>Derrimut VIC 3026 Australia</div>
                <div>Phone: (03) 9267 2677</div>
                <div>Fax: (03) 9267 2600</div>
              </Box>
            </Stack>
          </Box>

          {/* supplier panel — the empty box on the blank form */}
          <Box sx={{
            flex: 1, minHeight: '30mm', border: '0.4mm solid #111', p: '3mm',
            fontSize: '9pt', lineHeight: 1.5,
          }}>
            <div style={{ fontSize: '7pt', letterSpacing: '0.5px', color: '#666', marginBottom: '1.5mm' }}>
              SUPPLIER
            </div>
            <div style={{ fontWeight: 800, fontSize: '11pt' }}>
              {supplier?.name?.toUpperCase() || po.supplier_code}
            </div>
            {supplier?.contact_name && <div>{supplier.contact_name}</div>}
            {supplier?.contact_email && <div>{supplier.contact_email}</div>}
            {supplier?.contact_phone && <div>{supplier.contact_phone}</div>}
            {supplier?.account_ref && <div>Account: {supplier.account_ref}</div>}
            {attention && <div style={{ marginTop: '2mm' }}>Attn: {attention}</div>}
          </Box>

          <Box sx={{ width: '62mm', flexShrink: 0, textAlign: 'right' }}>
            <Typography sx={{ fontSize: '17pt', fontWeight: 800, letterSpacing: '0.3px', lineHeight: 1.1 }}>
              PURCHASE ORDER
            </Typography>
            <Box sx={{ fontSize: '8.5pt', mt: '3mm', lineHeight: 1.6 }}>
              <div><strong>Order no.</strong>&nbsp;&nbsp;{po.po_number}</div>
              <div><strong>Order date</strong>&nbsp;&nbsp;{fmtDate(po.order_date) || '—'}</div>
              <div><strong>Required by</strong>&nbsp;&nbsp;{fmtDate(po.expected_date) || '—'}</div>
            </Box>
          </Box>
        </Stack>

        {/* ---- column bar ---- */}
        <Box sx={{ mt: '5mm', display: 'flex', bgcolor: FORM_RED, color: '#fff',
          fontSize: '8.5pt', fontWeight: 500, py: '1.6mm' }}>
          <Cell w="26mm" pad>Date</Cell>
          <Cell w="24mm">Quantity</Cell>
          <Cell grow>Description</Cell>
          <Cell w="24mm" right>Unit Price</Cell>
          <Cell w="26mm" right>Amount</Cell>
          <Cell w="22mm" right>G.S.T.</Cell>
          <Cell w="28mm" right padEnd>Total</Cell>
        </Box>

        {/* ---- body ---- */}
        <Box sx={{
          flex: 1, border: '0.3mm solid #111', borderTop: 'none',
          display: 'flex', flexDirection: 'column',
        }}>
          {lines.length === 0 ? (
            <Box sx={{ p: '4mm', fontSize: '9pt', color: '#666' }}>This order has no lines.</Box>
          ) : lines.map((l, i) => {
            const kg = Number(l.qty_ordered_kg ?? 0);
            const rate = Number(l.unit_cost_per_kg ?? 0);
            const amount = kg * rate;
            const lineGst = amount * GST_RATE;
            const anyLine = l as InkPoLine & {
              effective_expected_date?: string | null;
              supplier_article_no?: string | null;
            };
            return (
              <Box key={l.id ?? i} sx={{
                display: 'flex', fontSize: '8.6pt', py: '1.8mm',
                borderBottom: '0.2mm solid #ddd', alignItems: 'flex-start',
              }}>
                <Cell w="26mm" pad>{fmtDate(anyLine.effective_expected_date ?? po.expected_date)}</Cell>
                <Cell w="24mm">{qtyText(l)}</Cell>
                <Cell grow>
                  <div style={{ fontWeight: 700 }}>{l.description || l.ink_key}</div>
                  {/* The supplier's OWN part number, set boldest of the three
                      identifiers on this line. It is the only one Flint can act
                      on — our ink_key means nothing to them — so on a printed
                      order it is the field that has to survive a photocopy
                      (Jack, 15/09). Falls back loudly rather than silently: a
                      missing article number is a real gap on a real order. */}
                  <div style={{ fontSize: '8.4pt', marginTop: '0.6mm' }}>
                    <span style={{ color: '#555' }}>Material no. </span>
                    {anyLine.supplier_article_no ? (
                      <span style={{ fontWeight: 800, letterSpacing: '0.2px' }}>
                        {anyLine.supplier_article_no}
                      </span>
                    ) : (
                      <span style={{ fontWeight: 700, color: FORM_RED }}>NOT ON FILE</span>
                    )}
                  </div>
                  <div style={{ fontSize: '7.4pt', color: '#555' }}>
                    {l.ink_key}{` · ${kg} kg`}
                  </div>
                </Cell>
                <Cell w="24mm" right>{rate ? `${money(rate)}/kg` : ''}</Cell>
                <Cell w="26mm" right>{money(amount)}</Cell>
                <Cell w="22mm" right>{money(lineGst)}</Cell>
                <Cell w="28mm" right padEnd>{money(amount + lineGst)}</Cell>
              </Box>
            );
          })}
        </Box>

        {/* ---- totals + footer ---- */}
        <Stack direction="row" alignItems="flex-end" sx={{ mt: '3mm' }}>
          <Box sx={{ flex: 1 }}>
            <IsoBadge />
          </Box>
          <Box sx={{ display: 'flex', border: '0.3mm solid #111', fontSize: '9pt' }}>
            <TotalCell label="Subtotal" value={money(subtotal)} />
            <TotalCell label="G.S.T." value={money(gst)} shaded />
            <TotalCell label="Total" value={money(total)} bold />
          </Box>
        </Stack>

        <Stack direction="row" sx={{ mt: '3mm', fontSize: '7pt', color: '#333' }}>
          <Box sx={{ flex: 1 }}>Issued: 18-SEP-2018</Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>F-093</Box>
          <Box sx={{ flex: 1, textAlign: 'right' }}>Version 2</Box>
        </Stack>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------

function Cell({ w, grow, right, pad, padEnd, children }: {
  w?: string; grow?: boolean; right?: boolean; pad?: boolean; padEnd?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Box sx={{
      width: grow ? undefined : w, flex: grow ? 1 : undefined, minWidth: 0,
      textAlign: right ? 'right' : 'left',
      pl: pad ? '3mm' : '2mm', pr: padEnd ? '3mm' : '2mm',
      boxSizing: 'border-box',
    }}>
      {children}
    </Box>
  );
}

function TotalCell({ label, value, shaded, bold }: {
  label: string; value: string; shaded?: boolean; bold?: boolean;
}) {
  return (
    <Box sx={{
      width: '30mm', px: '2.5mm', py: '2mm', textAlign: 'right',
      bgcolor: shaded ? '#ececec' : '#fff',
      borderLeft: '0.3mm solid #111',
      '&:first-of-type': { borderLeft: 'none' },
    }}>
      <div style={{ fontSize: '6.8pt', letterSpacing: '0.4px', color: '#666' }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: bold ? '11pt' : '9.5pt', fontWeight: bold ? 800 : 600 }}>{value}</div>
    </Box>
  );
}

/**
 * The ABARIS mark: green square, two stacked green discs, wordmark under.
 *
 * Geometry traced off F-093; the WORDMARK IS NOT THE REAL LOGOTYPE — it is a
 * heavy condensed sans stand-in, because no vector or raster Abaris logo exists
 * anywhere in this repository. Replace this component with the real artwork when
 * there is one; nothing else in the document depends on it.
 */
function AbarisMark() {
  return (
    <Box sx={{ width: '30mm', flexShrink: 0 }}>
      <svg viewBox="0 0 120 78" width="100%" role="img" aria-label="Abaris">
        <rect x="1" y="1" width="52" height="38" fill={ABARIS_GREEN} stroke="#111" strokeWidth="1.5" />
        <circle cx="68" cy="12" r="10" fill={ABARIS_GREEN} />
        <circle cx="68" cy="34" r="10" fill={ABARIS_GREEN} />
        <text x="0" y="72" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900"
          fontSize="26" letterSpacing="-0.5" fill="#111">ABARIS</text>
      </svg>
    </Box>
  );
}

/** The ISO 9001 certification badge that sits bottom-left on the form. */
function IsoBadge() {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-end">
      <Box sx={{
        bgcolor: FORM_RED, color: '#fff', px: '1.2mm', py: '2mm',
        fontSize: '6pt', fontWeight: 700, letterSpacing: '0.3px',
        writingMode: 'vertical-rl', transform: 'rotate(180deg)',
      }}>
        Certified System
      </Box>
      <Box sx={{ fontSize: '7pt', lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700 }}>Quality</div>
        <div>ISO 9001</div>
        <div style={{ color: FORM_RED, fontWeight: 700 }}>SAI GLOBAL</div>
      </Box>
    </Stack>
  );
}
